import {
  createStaffAnalyticsApi,
  createStaffAnalyticsExploreApi,
  createStaffAnalyticsEventsApi,
  createStaffTeamApi,
} from "@lafayette/api-client";
import { ANALYTICS_DEFAULT_TIME_ZONE } from "@lafayette/schemas";
import { useMemo } from "react";

import { currentAccessToken } from "../catalog/admin-runtime";
import { getAdminPublicConfig } from "../config/public-config";
import type { AnalyticsRuntime } from "./runtime";

/**
 * The zone of the browser, or the default zone when the browser gives no zone (an empty or missing name). A name that the database does not know
 * is NOT replaced here: it is sent as it is, and the API answers 400 `analytics.window_invalid`. The list of the browser is not the list of the database.
 */
function browserZone(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return typeof zone === "string" && zone.length > 0 ? zone : ANALYTICS_DEFAULT_TIME_ZONE;
}

/** The runtime of the real admin app: the typed clients over `fetch`. The bearer token comes from the function the other areas use. */
function createAdminAnalyticsRuntime(): AnalyticsRuntime {
  const config = getAdminPublicConfig();
  const options = { baseUrl: config.apiUrl.toString(), getAccessToken: currentAccessToken };
  const team = createStaffTeamApi(options);
  return {
    api: createStaffAnalyticsApi(options),
    explore: createStaffAnalyticsExploreApi(options),
    events: createStaffAnalyticsEventsApi(options),
    timeZone: browserZone,
    findConversationId: async (customerId, callOptions) =>
      (await team.customerSupport(customerId, callOptions)).conversation?.id ?? null,
  };
}

/** The runtime of the Analytics area for the app: the one a test passes, else the real one. Made once for the life of the app. */
export function useAnalyticsRuntimeOf(given: AnalyticsRuntime | undefined): AnalyticsRuntime {
  return useMemo(() => given ?? createAdminAnalyticsRuntime(), [given]);
}
